import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle2, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/lib/supabase';
import type { OnboardingFormSchema } from './types';

interface FormData {
  client_name: string;
  step_title: string;
  step_description: string | null;
  form_schema: OnboardingFormSchema | null;
  existing_data: Record<string, string>;
  expires_at: string;
}

interface FormError {
  error: 'token_invalid' | 'token_used' | 'token_expired' | 'token_without_step';
}

const ERROR_LABELS: Record<FormError['error'], string> = {
  token_invalid: 'Link inválido. Verifique se o endereço está correto ou solicite um novo.',
  token_used: 'Este link já foi utilizado. Entre em contato com seu consultor.',
  token_expired: 'Este link expirou. Solicite um novo ao seu consultor.',
  token_without_step: 'Link com configuração incompleta. Solicite um novo.',
};

export function PublicOnboardingFormPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<FormData | null>(null);
  const [error, setError] = useState<FormError['error'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!token) {
      setError('token_invalid');
      setLoading(false);
      return;
    }
    void supabase
      .rpc('get_onboarding_form_by_token', { p_token: token })
      .then(({ data: result, error: rpcError }) => {
        if (rpcError) {
          setError('token_invalid');
          setLoading(false);
          return;
        }
        const r = result as unknown as FormData | FormError;
        if ('error' in r) {
          setError(r.error);
          setLoading(false);
          return;
        }
        setData(r);
        setValues(r.existing_data ?? {});
        setLoading(false);
      });
  }, [token]);

  const handleChange = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !data) return;
    setSubmitting(true);
    try {
      const { data: result, error: rpcError } = await supabase.rpc('submit_onboarding_form', {
        p_token: token,
        p_data: values,
      });
      if (rpcError) {
        toast.error('Erro ao enviar: ' + rpcError.message);
        return;
      }
      const r = result as unknown as { success: boolean } | FormError;
      if ('error' in r) {
        setError(r.error);
        return;
      }
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary py-12">
      <div className="mx-auto max-w-2xl px-4">
        <div className="mb-6 flex items-center justify-center">
          <img
            src="/identidade-visual/logo/vetor/logo-horizontal.svg"
            alt="Clinance"
            className="h-10"
          />
        </div>

        {loading ? (
          <Card>
            <CardContent className="flex justify-center p-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        ) : error ? (
          <Card>
            <CardContent className="space-y-3 p-8 text-center">
              <p className="font-medium text-foreground">Não foi possível abrir o formulário</p>
              <p className="text-sm text-muted-foreground">{ERROR_LABELS[error]}</p>
            </CardContent>
          </Card>
        ) : done ? (
          <Card>
            <CardContent className="space-y-3 p-8 text-center">
              <CheckCircle2 className="mx-auto h-12 w-12 text-income" />
              <p className="font-brand text-xl font-bold text-foreground">Tudo certo!</p>
              <p className="text-sm text-muted-foreground">
                Suas informações foram enviadas para o time da Clinance. Em breve seu consultor
                entrará em contato.
              </p>
            </CardContent>
          </Card>
        ) : data ? (
          <Card>
            <CardContent className="space-y-5 p-6">
              <div className="border-b border-border pb-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Onboarding · {data.client_name}
                </p>
                <h1 className="mt-1 font-brand text-xl font-bold text-foreground">
                  <FileText className="mr-2 inline h-5 w-5 text-primary" />
                  {data.step_title}
                </h1>
                {data.step_description && (
                  <p className="mt-1 text-sm text-muted-foreground">{data.step_description}</p>
                )}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {data.form_schema?.fields.map((field) => (
                  <div key={field.name} className="space-y-1.5">
                    <Label htmlFor={field.name}>
                      {field.label}
                      {field.required && <span className="ml-1 text-expense">*</span>}
                    </Label>
                    {field.type === 'textarea' ? (
                      <Textarea
                        id={field.name}
                        value={values[field.name] ?? ''}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        required={!!field.required}
                        rows={3}
                      />
                    ) : (
                      <Input
                        id={field.name}
                        type={
                          field.type === 'email' ? 'email' : field.type === 'url' ? 'url' : 'text'
                        }
                        value={values[field.name] ?? ''}
                        onChange={(e) => handleChange(field.name, e.target.value)}
                        required={!!field.required}
                      />
                    )}
                  </div>
                ))}

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="mr-2 animate-spin" />
                      Enviando…
                    </>
                  ) : (
                    'Enviar'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : null}

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Os dados enviados aqui ficam disponíveis apenas para o time Clinance vinculado à sua
          clínica. Pergunte ao seu consultor se tiver dúvidas.
        </p>
      </div>
    </div>
  );
}
